import { useEffect, useMemo, useState, useCallback } from "react";

export default function useWebsiteVisitSelection({
  sortedRows,
  getGroupId,
  resetKey,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const selectedCount = selectedIds.size;

  const allSelectableIds = useMemo(
    () => sortedRows.map((g) => getGroupId(g)).filter(Boolean),
    [sortedRows, getGroupId],
  );

  const isAllSelected = useMemo(
    () =>
      allSelectableIds.length > 0 &&
      allSelectableIds.every((id) => selectedIds.has(id)),
    [allSelectableIds, selectedIds],
  );

  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  const handleToggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (isAllSelected) {
        allSelectableIds.forEach((id) => next.delete(id));
      } else {
        allSelectableIds.forEach((id) => next.add(id));
      }

      return next;
    });
  }, [allSelectableIds, isAllSelected]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [resetKey]);

  return {
    selectedIds,
    setSelectedIds,
    selectedCount,
    allSelectableIds,
    isAllSelected,
    isSomeSelected,
    handleToggleOne,
    handleToggleAll,
  };
}
